/**
 * natureConfig — the declarative per-biome vegetation data layer for the Tree &
 * Nature system. Maps each biome id to a `BiomeVegetation` profile (density +
 * per-kind specs), so a level swapping biomes re-skins its vegetation in one
 * resolved lookup — the same "family base + per-biome override" pattern the
 * BiomeTextureModule uses for tile tints.
 *
 * DESIGN — every field optional + defaulted, resolves to a flat spec object.
 * JSON/serde-friendly so it maps 1:1 to a Rust `struct` (the lift-ready
 * checklist §2). Pure data + selectors — no renderer wiring here.
 *
 * The 8 family bases carry a biome-character-typical vegetation palette (cold =
 * sparse hardy shrubs, temperate = lush forest, hot = dry thorn, …); each biome
 * id is mapped to a family, then layered with a thin per-biome override (its
 * distinguishing foliage tints + density). The resolver merges:
 * biome-override kind → family kind → family density default → global default.
 */
import type { BiomeTileFamily } from '../../rendering/textures/BiomeTextureModule';
import type { PropKind } from './propTextures';
import { TREE_FREE_SCENERY_BIOMES, isTreeFreeSceneryMap } from '../../../shared/scene/connectedSceneryMaps';

/** The three core vegetation kinds the system plants on platforms. */
export type CoreVegetationKind = 'tree' | 'bush' | 'grass';

/** Everything the nature system plants — core vegetation + environmental
 *  props (ENVIRONMENTAL_PROPS.md). Props are just extra kinds in the same
 *  weighted mix; biomes opt in via their profile (see propTextures.ts header
 *  for the seam decision). */
export type VegetationKind = CoreVegetationKind | PropKind;

/** A single vegetation kind's render-ready spec (resolved, all fields present). */
export interface VegetationSpec {
  /** Foliage tint palette (0xRRGGBB) — one picked per plant by seed. */
  palette: number[];
  /** Plant height range (px) — width is derived from the kind's aspect ratio. */
  sizeMin: number;
  sizeMax: number;
  /** Relative weight in the biome's kind mix (higher = more frequent). */
  weight: number;
  /** Base alpha (0..1). */
  alpha: number;
}

/** A biome's full vegetation profile — density + per-kind specs. */
export interface BiomeVegetation {
  /** Expected plants per 100px of standable platform width (the density dial). */
  density: number;
  /** Per-kind specs. A kind absent from `kinds` is never planted. */
  kinds: Partial<Record<VegetationKind, VegetationSpec>>;
}

/** Author-time config — every field optional, merged to a flat resolved profile. */
export interface BiomeVegetationInput {
  density?: number;
  kinds?: Partial<Record<VegetationKind, Partial<VegetationSpec>>>;
}

// ─── family base profiles (8 biome characters) ──────────────────────────────
const FAMILY_VEGETATION: Record<BiomeTileFamily, BiomeVegetationInput> = {
  cold: {
    density: 0.4,
    kinds: {
      tree: { palette: [0x4a6a4a, 0x5a7a5a], sizeMin: 60, sizeMax: 90, weight: 1, alpha: 0.9 },
      bush: { palette: [0x5a7a5a, 0x6a8a6a], sizeMin: 20, sizeMax: 35, weight: 2, alpha: 0.9 },
      grass: { palette: [0x7a9a7a, 0x8aaa8a], sizeMin: 10, sizeMax: 18, weight: 3, alpha: 0.85 },
    },
  },
  hot: {
    density: 0.3,
    kinds: {
      tree: { palette: [0x6a5a3a, 0x7a6a4a], sizeMin: 50, sizeMax: 80, weight: 1, alpha: 0.85 },
      bush: { palette: [0x8a7a4a, 0x9a8a5a], sizeMin: 18, sizeMax: 30, weight: 2, alpha: 0.85 },
      grass: { palette: [0xaa9a5a, 0xbaaa6a], sizeMin: 8, sizeMax: 16, weight: 4, alpha: 0.8 },
    },
  },
  toxic: {
    density: 0.5,
    kinds: {
      tree: { palette: [0x4a6a3a, 0x5a8a4a], sizeMin: 55, sizeMax: 85, weight: 1, alpha: 0.85 },
      bush: { palette: [0x6a8a4a, 0x7a9a5a], sizeMin: 20, sizeMax: 32, weight: 3, alpha: 0.85 },
      grass: { palette: [0x5a9a4a, 0x6aaa5a], sizeMin: 10, sizeMax: 18, weight: 3, alpha: 0.8 },
    },
  },
  temperate: {
    density: 0.8,
    kinds: {
      tree: { palette: [0x2a6a2a, 0x3a7a3a, 0x4a8a4a], sizeMin: 70, sizeMax: 110, weight: 3, alpha: 0.95 },
      bush: { palette: [0x3a7a3a, 0x4a8a4a], sizeMin: 22, sizeMax: 38, weight: 2, alpha: 0.9 },
      grass: { palette: [0x4a8a3a, 0x5a9a4a], sizeMin: 12, sizeMax: 22, weight: 4, alpha: 0.9 },
    },
  },
  dark: {
    density: 0.35,
    kinds: {
      tree: { palette: [0x2a3a2a, 0x3a4a3a], sizeMin: 60, sizeMax: 95, weight: 1, alpha: 0.85 },
      bush: { palette: [0x3a4a3a, 0x4a5a4a], sizeMin: 18, sizeMax: 32, weight: 2, alpha: 0.85 },
      grass: { palette: [0x4a5a4a, 0x5a6a5a], sizeMin: 8, sizeMax: 16, weight: 3, alpha: 0.8 },
    },
  },
  aquatic: {
    density: 0.45,
    kinds: {
      tree: { palette: [0x3a7a6a, 0x4a8a7a], sizeMin: 50, sizeMax: 80, weight: 1, alpha: 0.85 },
      bush: { palette: [0x2a8a6a, 0x3a9a7a], sizeMin: 18, sizeMax: 30, weight: 2, alpha: 0.85 },
      grass: { palette: [0x1a9a8a, 0x2aaa9a], sizeMin: 12, sizeMax: 22, weight: 4, alpha: 0.85 },
    },
  },
  bright: {
    density: 0.75,
    kinds: {
      tree: { palette: [0x3a8a3a, 0x4a9a4a, 0x5aaa5a], sizeMin: 65, sizeMax: 105, weight: 3, alpha: 0.95 },
      bush: { palette: [0x4a9a3a, 0x5aaa4a], sizeMin: 22, sizeMax: 38, weight: 2, alpha: 0.9 },
      grass: { palette: [0x5a9a3a, 0x6aaa4a, 0x7aba5a], sizeMin: 12, sizeMax: 22, weight: 4, alpha: 0.9 },
    },
  },
  ethereal: {
    density: 0.4,
    kinds: {
      tree: { palette: [0x6a5a8a, 0x7a6a9a, 0x8a7aaa], sizeMin: 60, sizeMax: 95, weight: 2, alpha: 0.85 },
      bush: { palette: [0x7a6a9a, 0x8a7aaa], sizeMin: 20, sizeMax: 34, weight: 2, alpha: 0.85 },
      grass: { palette: [0x8a7aaa, 0x9a8aba], sizeMin: 10, sizeMax: 18, weight: 3, alpha: 0.8 },
    },
  },
};

// ─── biome → family (mirrors BiomeTextureModule's BIOME_FAMILY) ─────────────
const BIOME_FAMILY: Record<string, BiomeTileFamily> = {
  cozy_forest: 'temperate', wickhaven: 'temperate', subtle: 'temperate', dungeon: 'dark', cave: 'dark',
  volcanic: 'hot', arctic: 'cold', underwater: 'aquatic', desert: 'hot',
  corrupted: 'toxic', swamp: 'toxic', fungal: 'toxic', jungle: 'temperate',
  meadow: 'bright', celestial: 'ethereal', autumn: 'hot', ashlands: 'dark',
  tempest: 'cold', coast: 'aquatic', radiant: 'bright', moor: 'cold',
  savanna: 'hot', skylands: 'bright', bamboo: 'temperate', abyss: 'dark',
  twilight: 'temperate', frostspire: 'cold', crimson: 'hot', petrified: 'dark',
  sakura: 'temperate', nexus: 'ethereal', arena: 'hot',
  sunken_drift: 'aquatic', sky_whale: 'ethereal', void_serpent: 'dark', fossil_graveyard: 'dark',
  pig_beach: 'bright', tidewend: 'temperate', brackenmoor: 'cold', coil_undercroft: 'dark',
  village_arrival: 'temperate', first_field: 'bright', tomb_waking: 'dark',
  tidewend_outskirts: 'cold', drowned_road: 'cold', lanternwood: 'dark',
  moonfall_hollow: 'dark', lanternroot_hollow: 'dark', moonfen_pasture: 'cold',
};

// ─── per-biome overrides (distinguishing foliage tints + density) ───────────
const BIOME_OVERRIDES: Record<string, BiomeVegetationInput> = {
  // Public service streets keep trees in the distant silhouette; quays are fully authored.
  wickhaven: { density: 0.2, kinds: {
    tree: { weight: 0 }, bush: { palette: [0x537366, 0x627c67], sizeMin: 14, sizeMax: 21, weight: 1, alpha: 0.72 },
    grass: { palette: [0x728569, 0x839474], sizeMin: 8, sizeMax: 12, weight: 3, alpha: 0.7 },
  } },
  tidewend_port: { density: 0, kinds: { grass: { weight: 0 } } },
  wickhaven_port: { density: 0, kinds: { grass: { weight: 0 } } },
  cozy_forest: { kinds: { tree: { palette: [0x2a6a2a, 0x3a8a3a] } } },
  volcanic: { density: 0.15, kinds: { tree: { palette: [0x4a3a2a, 0x5a4a3a], weight: 1 }, bush: { palette: [0x6a3a2a, 0x8a4a2a] } } },
  arctic: { density: 0.25, kinds: { tree: { palette: [0x5a7a8a, 0x6a8a9a] }, grass: { palette: [0xbfe9ff, 0xdcefff] } } },
  desert: { density: 0.12, kinds: { tree: { palette: [0x8a7a4a, 0x9a8a5a], sizeMax: 70 }, bush: { palette: [0xaa9a5a, 0xbaaa6a] }, grass: { palette: [0xc4b86a, 0xd4c87a] } } },
  // Fungal ledges need a small understory beneath authored mushrooms, not
  // miniature outdoor trees. Other toxic-family biomes keep their own mix.
  fungal: { density: 0.36, kinds: {
    tree: { palette: [0x6a3a8a, 0x8a4aaa], weight: 0 },
    bush: { palette: [0xd633ff, 0xb84acc], sizeMin: 12, sizeMax: 20, weight: 1 },
    grass: { palette: [0x4a8f3e, 0x5a9f4e], sizeMin: 6, sizeMax: 12, weight: 2 },
  } },
  jungle: { density: 1.0, kinds: { tree: { palette: [0x1a5a1a, 0x2a6a2a], sizeMax: 120, weight: 4 }, bush: { palette: [0x2a7a2a, 0x3a8a3a], weight: 3 } } },
  meadow: { density: 0.9, kinds: { tree: { palette: [0x4a8a3a, 0x5a9a4a], weight: 2 }, grass: { palette: [0x5a9a3a, 0x7aba5a, 0x9aca7a], weight: 5 } } },
  sakura: { kinds: { tree: { palette: [0xffd0e0, 0xf6cdda, 0xe8a8c8] }, bush: { palette: [0xf6cdda, 0xe8a8c8] }, grass: { palette: [0x5a9a4a, 0x6aaa5a] } } },
  autumn: { kinds: { tree: { palette: [0xd4691e, 0xe6a85a, 0xc4501e] }, bush: { palette: [0xe6a85a, 0xd4691e] }, grass: { palette: [0xc4a05a, 0xd4b06a] } } },
  swamp: { density: 0.6, kinds: { tree: { palette: [0x3a5a2a, 0x4a6a3a], weight: 1 }, bush: { palette: [0x4a6a3a, 0x5a7a4a], weight: 3 }, grass: { palette: [0x5a7a3a, 0x6a8a4a] } } },
  ashlands: { density: 0.1, kinds: { tree: { palette: [0x3a3a3a, 0x4a4a4a], weight: 1 }, bush: { palette: [0x4a4a4a, 0x5a5a5a] }, grass: { palette: [0x5a5a5a, 0x6a6a6a] } } },
  // On-ramp exteriors: the village reads inhabited and warm; the field stays
  // open and grass-led so the first pigs remain legible combat targets.
  village_arrival: {
    density: 0.65,
    kinds: {
      tree: { palette: [0x52733f, 0x66834b], weight: 1.6 },
      bush: { palette: [0x66834b, 0x7b9558], weight: 1.8 },
      grass: { palette: [0x789752, 0x91aa63], weight: 3.8 },
      village_house: { palette: [0xd4a868, 0xc89c60], sizeMin: 84, sizeMax: 108, weight: 0, alpha: 0.95 },
      village_well: { palette: [0x8a8a8a], sizeMin: 54, sizeMax: 64, weight: 0, alpha: 0.95 },
      lantern_post: { palette: [0xffcc00], sizeMin: 84, sizeMax: 100, weight: 0, alpha: 1 },
      autumn_leaf: { palette: [0xcc7733, 0xb85f28, 0xd49a45], sizeMin: 8, sizeMax: 13, weight: 0.9, alpha: 0.9 },
    },
  },
  first_field: {
    density: 0.9,
    kinds: {
      tree: { palette: [0x4f8739, 0x67a044], weight: 1.25 },
      bush: { palette: [0x5f963f, 0x78aa4f], weight: 2 },
      grass: { palette: [0x6fa447, 0x88b957, 0xa0c96b], weight: 6 },
      pig_pen: { palette: [0x80694e], weight: 0 },
      fallen_log: { palette: [0x3b2c22], weight: 0 },
      lantern_post: { palette: [0xb68b55], weight: 0 },
      gate: { palette: [0x5d4a38], weight: 0 },
    },
  },
  // The opening vault is an interior. All landmarks are deterministic scene
  // props; the profile exists only to mount that authored layer, never to
  // scatter outdoor vegetation across the tomb floor.
  tomb_waking: {
    density: 0,
    kinds: {
      tree: { palette: [0x232126], weight: 0 },
      bush: { palette: [0x2b282f], weight: 0 },
      grass: { palette: [0x353139], weight: 0 },
      colonnade_column: { palette: [0x302c32], weight: 0 },
      broken_pillar: { palette: [0x302d35], weight: 0 },
      dais: { palette: [0x3b363f], weight: 0 },
      spiral_ledge: { palette: [0x29262e], weight: 0 },
      moor_stone: { palette: [0x354247], weight: 0 },
    },
  },
  // Hollow Coil cozy→spooky gradient — vegetation thins and desaturates.
  // pig_beach carries the Tier-1 environmental-prop mix (ENVIRONMENTAL_PROPS.md):
  // palms are THE beach identifier (3-5/map); rocks dot the waterline; tide
  // pools + trinkets are rare quest accents; pens mark the pig spawn areas.
  // Density raised 0.7 → 0.85 so props ride the mix without starving the veg.
  pig_beach: {
    density: 0.85,
    kinds: {
      tree: { palette: [0x6f9e86, 0x5a8a6a], weight: 2 },
      bush: { palette: [0x6f9e86, 0x8aba9a] },
      grass: { palette: [0x8aba6a, 0x9aca7a] },
      palm_tree: { palette: [0x6f9e86], sizeMin: 80, sizeMax: 120, weight: 2, alpha: 0.95 },
      beach_rock: { palette: [0xa0a0a0, 0x8f8f8f], sizeMin: 18, sizeMax: 32, weight: 1.2, alpha: 0.95 },
      tide_pool: { palette: [0x4a9acf], sizeMin: 12, sizeMax: 18, weight: 0.4, alpha: 0.9 },
      trinket: { palette: [0xf5e3b0, 0xa0d8f0, 0x8b7355], sizeMin: 8, sizeMax: 14, weight: 0.8, alpha: 1 },
      pig_pen: { palette: [0xa0856a], sizeMin: 34, sizeMax: 44, weight: 0.5, alpha: 0.95 },
    },
  },
  // Tidewend's buildings, well, dock, lamps, and gate are fixed navigation
  // landmarks in scene.natureProps. Only vegetation and fallen leaves scatter,
  // so the town keeps its authored silhouette across seeds and sessions.
  tidewend: {
    density: 0.75,
    kinds: {
      tree: { palette: [0x4a6a3a, 0x5a7a4a], weight: 2 },
      bush: { palette: [0x5a7a4a, 0x6a8a5a] },
      grass: { palette: [0x6a8a4a, 0x7a9a5a] },
      village_house: { palette: [0xd4a868, 0xc89c60], sizeMin: 84, sizeMax: 112, weight: 0, alpha: 0.95 },
      village_well: { palette: [0x8a8a8a], sizeMin: 54, sizeMax: 64, weight: 0, alpha: 0.95 },
      lantern_post: { palette: [0xffcc00], sizeMin: 84, sizeMax: 104, weight: 0, alpha: 1 },
      dock: { palette: [0x8b7355], sizeMin: 38, sizeMax: 50, weight: 0, alpha: 0.95 },
      autumn_leaf: { palette: [0xcc6600, 0xb35900, 0xa64d1a], sizeMin: 8, sizeMax: 13, weight: 1.2, alpha: 0.9 },
    },
  },
  // The last farms and the drowned road steadily lose the town's warm detail.
  // Large story silhouettes are authored; only sparse ground texture scatters.
  tidewend_outskirts: {
    density: 0.42,
    kinds: {
      tree: { palette: [0x405342, 0x4b5f49], weight: 0.6, alpha: 0.78 },
      bush: { palette: [0x485a46, 0x56684f], weight: 1.2, alpha: 0.78 },
      grass: { palette: [0x596b50, 0x6b7858], weight: 3.5, alpha: 0.72 },
      village_house: { palette: [0x745b4f], weight: 0 },
      lantern_post: { palette: [0xa97949], weight: 0 },
      pig_pen: { palette: [0x67543f], weight: 0 },
      fallen_log: { palette: [0x35291f], weight: 0 },
      gate: { palette: [0x405047], weight: 0 },
    },
  },
  drowned_road: {
    density: 0.2,
    kinds: {
      tree: { palette: [0x34484a], weight: 0 },
      bush: { palette: [0x405254], weight: 0.3, alpha: 0.65 },
      grass: { palette: [0x4d6260, 0x5c706a], weight: 2.2, alpha: 0.62 },
      fallen_log: { palette: [0x342a27], weight: 0 },
      moor_stone: { palette: [0x43545a], weight: 0 },
      mist_shelf: { palette: [0x344f58], weight: 0 },
      lantern_post: { palette: [0x8d764f], weight: 0 },
      gate: { palette: [0x3b4b46], weight: 0 },
    },
  },
  lanternwood: {
    density: 0.27,
    kinds: {
      tree: { palette: [0x26372f, 0x304139], weight: 0.5, alpha: 0.72 },
      bush: { palette: [0x304139, 0x3a4a40], weight: 0.8, alpha: 0.7 },
      grass: { palette: [0x405044, 0x4b5848], weight: 1.5, alpha: 0.66 },
      lantern_post: { palette: [0xa66f40], weight: 0 },
      fallen_log: { palette: [0x30251f], weight: 0 },
      dead_bracken: { palette: [0x2d2a21], weight: 1.6, alpha: 0.78 },
      gate: { palette: [0x394b43], weight: 0 },
      gnarled_tree: { palette: [0x304139, 0x26372f], weight: 0 },
    },
  },
  // Cavern detail is authored, including supported ceiling attachments. Future
  // ledges must not inherit outdoor trees and grass from the dark family.
  lanternroot_hollow: {
    density: 0,
    kinds: {
      tree: { weight: 0 }, bush: { weight: 0 }, grass: { weight: 0 },
      gnarled_tree: { palette: [0x3e4032], weight: 0 },
      bioluminescent_fungus: { palette: [0x77a185], weight: 0 },
    },
  },
  // Brackenmoor's seeded layer is texture only: dead bracken, low stones,
  // fallen logs and sparse grass. Structural/quest props are deliberately
  // weight-zero here and placed through scene.natureProps instead. A gate or
  // journal page chosen at random on a combat ledge makes the terrain lie.
  // Rounded family trees/bushes are also suppressed: this is open, dying moor,
  // not a recoloured forest.
  brackenmoor: {
    density: 0.34,
    kinds: {
      tree: { palette: [0x5a6a5a, 0x6a7a6a], weight: 0 },
      bush: { palette: [0x6a7a5a, 0x7a8a6a], weight: 0 },
      grass: { palette: [0x53654f, 0x60715a], weight: 1.8, alpha: 0.72 },
      dead_bracken: { palette: [0x3a2a1a], sizeMin: 32, sizeMax: 48, weight: 3, alpha: 0.9 },
      moor_stone: { palette: [0x46574e], sizeMin: 24, sizeMax: 34, weight: 1, alpha: 0.92 },
      mist_shelf: { palette: [0x55665b], sizeMin: 60, sizeMax: 90, weight: 0, alpha: 0.85 },
      fallen_log: { palette: [0x3a2a1a], sizeMin: 60, sizeMax: 82, weight: 0.75, alpha: 0.95 },
      gate: { palette: [0x4a3a2a], sizeMin: 60, sizeMax: 76, weight: 0, alpha: 1 },
      journal_page: { palette: [0xe8d8a0], sizeMin: 24, sizeMax: 30, weight: 0, alpha: 1 },
    },
  },
  // Interior landmarks are authored in scene.natureProps so navigation and
  // story silhouettes never move with a random seed. Only small fungus may be
  // scattered procedurally; outdoor trees/bushes/grass do not belong inside
  // the lighthouse shell.
  coil_undercroft: {
    density: 0.18,
    kinds: {
      tree: { palette: [0x1a2a1a, 0x2a3a2a], weight: 0 },
      bush: { palette: [0x2a3a2a, 0x3a4a3a], weight: 0 },
      grass: { palette: [0x2a3a2a, 0x3a4a2a], weight: 0 },
      colonnade_column: { palette: [0x1a2a18], sizeMin: 100, sizeMax: 150, weight: 0, alpha: 0.95 },
      broken_pillar: { palette: [0x1a2a18], sizeMin: 50, sizeMax: 70, weight: 0, alpha: 0.95 },
      dais: { palette: [0x2a3a28], sizeMin: 50, sizeMax: 70, weight: 0, alpha: 1 },
      spiral_ledge: { palette: [0x1a2a18], sizeMin: 55, sizeMax: 80, weight: 0, alpha: 0.9 },
      bioluminescent_fungus: { palette: [0x76985a, 0x8ea862], sizeMin: 10, sizeMax: 16, weight: 1.2, alpha: 1 },
    },
  },
};

/** The global fallback profile (sparse temperate) for unknown biomes. */
const DEFAULT_VEGETATION: BiomeVegetation = {
  density: 0.3,
  kinds: {
    tree: { palette: [0x3a6a3a], sizeMin: 60, sizeMax: 90, weight: 1, alpha: 0.9 },
    bush: { palette: [0x4a7a4a], sizeMin: 20, sizeMax: 32, weight: 2, alpha: 0.85 },
    grass: { palette: [0x5a8a4a], sizeMin: 10, sizeMax: 18, weight: 3, alpha: 0.85 },
  },
};

/** Default per-kind spec fields (merged when a family/override omits them).
 *  Prop defaults mirror the ENVIRONMENTAL_PROPS.md spec primaries — a biome
 *  opting into a prop kind with a partial spec still lands on-palette. */
const DEFAULT_KIND: Record<VegetationKind, VegetationSpec> = {
  sign: { palette: [0x80674f], sizeMin: 44, sizeMax: 44, weight: 0, alpha: 1 },
  tree: { palette: [0x3a6a3a], sizeMin: 70, sizeMax: 100, weight: 2, alpha: 0.9 },
  bush: { palette: [0x4a7a4a], sizeMin: 20, sizeMax: 34, weight: 2, alpha: 0.85 },
  grass: { palette: [0x5a8a4a], sizeMin: 12, sizeMax: 20, weight: 3, alpha: 0.85 },
  palm_tree: { palette: [0x6f9e86], sizeMin: 80, sizeMax: 120, weight: 2, alpha: 0.95 },
  beach_rock: { palette: [0xa0a0a0], sizeMin: 18, sizeMax: 32, weight: 1, alpha: 0.95 },
  tide_pool: { palette: [0x4a9acf], sizeMin: 12, sizeMax: 18, weight: 0.5, alpha: 0.9 },
  trinket: { palette: [0xf5e3b0], sizeMin: 8, sizeMax: 14, weight: 1, alpha: 1 },
  pig_pen: { palette: [0xa0856a], sizeMin: 34, sizeMax: 44, weight: 0.5, alpha: 0.95 },
  village_house: { palette: [0xd4a868], sizeMin: 72, sizeMax: 96, weight: 1, alpha: 0.95 },
  village_well: { palette: [0x8a8a8a], sizeMin: 52, sizeMax: 62, weight: 0.25, alpha: 0.95 },
  lantern_post: { palette: [0xffcc00], sizeMin: 80, sizeMax: 100, weight: 0.9, alpha: 1 },
  dock: { palette: [0x8b7355], sizeMin: 38, sizeMax: 50, weight: 0.3, alpha: 0.95 },
  autumn_leaf: { palette: [0xcc6600], sizeMin: 8, sizeMax: 13, weight: 1, alpha: 0.9 },
  dead_bracken: { palette: [0x3a2a1a], sizeMin: 32, sizeMax: 48, weight: 1, alpha: 0.9 },
  moor_stone: { palette: [0x55665b], sizeMin: 26, sizeMax: 38, weight: 0.5, alpha: 0.95 },
  mist_shelf: { palette: [0x55665b], sizeMin: 60, sizeMax: 90, weight: 0.2, alpha: 0.85 },
  fallen_log: { palette: [0x3a2a1a], sizeMin: 70, sizeMax: 100, weight: 0.4, alpha: 0.95 },
  gate: { palette: [0x4a3a2a], sizeMin: 60, sizeMax: 76, weight: 0.08, alpha: 1 },
  journal_page: { palette: [0xe8d8a0], sizeMin: 24, sizeMax: 30, weight: 0.15, alpha: 1 },
  colonnade_column: { palette: [0x1a2a18], sizeMin: 100, sizeMax: 150, weight: 2, alpha: 0.95 },
  broken_pillar: { palette: [0x1a2a18], sizeMin: 50, sizeMax: 70, weight: 0.15, alpha: 0.95 },
  dais: { palette: [0x2a3a28], sizeMin: 50, sizeMax: 70, weight: 0.05, alpha: 1 },
  spiral_ledge: { palette: [0x1a2a18], sizeMin: 55, sizeMax: 80, weight: 0.3, alpha: 0.9 },
  bioluminescent_fungus: { palette: [0x76985a, 0x8ea862], sizeMin: 10, sizeMax: 16, weight: 1.2, alpha: 1 },
  gnarled_tree: { palette: [0x304139], sizeMin: 120, sizeMax: 160, weight: 0, alpha: 0.92 },
};

/** Default family density when a family base omits it. */
const DEFAULT_DENSITY = 0.4;

function resolveKind(kind: VegetationKind, partial?: Partial<VegetationSpec>): VegetationSpec {
  const base = DEFAULT_KIND[kind];
  if (!partial) return { ...base };
  return {
    palette: partial.palette ?? base.palette,
    sizeMin: partial.sizeMin ?? base.sizeMin,
    sizeMax: partial.sizeMax ?? base.sizeMax,
    weight: partial.weight ?? base.weight,
    alpha: partial.alpha ?? base.alpha,
  };
}

/** Resolve a biome's full vegetation profile (family base + per-biome override).
 *  Falls back to the global default for unknown biome ids. */
export function resolveBiomeVegetation(biome: string | null | undefined): BiomeVegetation {
  if (!biome) return DEFAULT_VEGETATION;
  const family = BIOME_FAMILY[biome];
  const override = BIOME_OVERRIDES[biome];
  if (!family && !override) return DEFAULT_VEGETATION;
  const base = family ? FAMILY_VEGETATION[family] : { density: DEFAULT_DENSITY, kinds: {} };
  const merged: BiomeVegetationInput = {
    density: override?.density ?? base.density ?? DEFAULT_DENSITY,
    kinds: { ...base.kinds, ...override?.kinds },
  };
  const kinds: Partial<Record<VegetationKind, VegetationSpec>> = {};
  for (const k of Object.keys(merged.kinds ?? {}) as VegetationKind[]) {
    const familyPartial = base.kinds?.[k];
    const overridePartial = override?.kinds?.[k];
    // Merge override ON TOP of family partial ON TOP of the global default kind.
    kinds[k] = resolveKind(k, { ...familyPartial, ...overridePartial });
  }
  if (Object.keys(kinds).length === 0) return DEFAULT_VEGETATION;
  return { density: merged.density ?? DEFAULT_DENSITY, kinds };
}

/** Connected caves, ruins, ice, lava and exposed cliffs keep a low understory
 * instead of outdoor trees. Exact map/biome admission leaves generic/editor consumers
 * unchanged; cloned specs never mutate the reusable biome profile. */
export function resolveMapVegetation(levelId: string, biome: string | null | undefined): BiomeVegetation {
  const profile = resolveBiomeVegetation(biome);
  if (!isTreeFreeSceneryMap(levelId) || biome !== TREE_FREE_SCENERY_BIOMES[levelId]) return profile;
  const kinds = { ...profile.kinds };
  if (kinds.tree) kinds.tree = { ...kinds.tree, weight: 0 };
  if (kinds.bush) kinds.bush = { ...kinds.bush,
    sizeMin: Math.min(kinds.bush.sizeMin, 12), sizeMax: Math.min(kinds.bush.sizeMax, 18),
    alpha: Math.min(kinds.bush.alpha, .75) };
  if (kinds.grass) kinds.grass = { ...kinds.grass,
    sizeMin: Math.min(kinds.grass.sizeMin, 6), sizeMax: Math.min(kinds.grass.sizeMax, 12),
    alpha: Math.min(kinds.grass.alpha, .75) };
  return { density: Math.min(profile.density, .22), kinds };
}

/** Does this biome have an authored vegetation profile? Renderers gate on this
 *  to decide whether to plant vegetation or leave the platform bare (graceful
 *  degradation — never plant the global default on a map that lacks a profile). */
export function biomeHasVegetation(biome: string | null | undefined): boolean {
  if (!biome) return false;
  return Boolean(BIOME_FAMILY[biome] || BIOME_OVERRIDES[biome]);
}

/** The set of biome ids with an authored vegetation override. */
export const NATURE_VEGETATION_BIOMES: readonly string[] = Object.keys(BIOME_OVERRIDES);
